/**
 * Cross-venue "Push leaderboard to displays" modal. Operator picks one or
 * many display-agents from any venue in the org; cloud REST fans the
 * leaderboard URL to each agent's hub via the `remote-set-composition`
 * command. Display-agents load the cloud's public token-gated URL in a
 * webview.
 *
 * Structural mirror of hub-ui's `PushLeaderboardModal.tsx` — the
 * differences are:
 *   - Display list source is the org-scoped /api/display-agents endpoint,
 *     not the single-hub one.
 *   - Each row labels "{name} — {venueName}" since the picker spans
 *     venues.
 *   - Status badge is driven by the `hub_agents.status` mirror synced
 *     from the hub via entity-delta.
 *   - The push response reports per-hub delivery: cloud-ui's toast
 *     surfaces "Pushed to N of M displays" + offline-hub callouts.
 */

import { useEffect, useMemo, useState } from "react";
import { Button, Modal, StatusBadge, useToast } from "@vms/ui-web";
import { useOrgDisplayAgents, type OrgDisplayAgent } from "../../api/display-agents";
import { usePushLeaderboardToDisplays } from "../../api/leaderboards";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organisationId: string;
  leaderboardId: string;
  leaderboardName: string;
}

export function PushToDisplaysModal({
  isOpen,
  onClose,
  organisationId,
  leaderboardId,
  leaderboardName,
}: Props): JSX.Element {
  const { data: displays, loading, error } = useOrgDisplayAgents(
    isOpen ? organisationId : undefined,
  );
  const push = usePushLeaderboardToDisplays();
  const { addToast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) setSelected(new Set());
  }, [isOpen]);

  const sortedDisplays = useMemo(() => {
    return [...displays].sort((a, b) => {
      // Online + hub-online first, then by venue, then by name.
      const aLive = a.status === "connected" && a.hubOnline ? 0 : 1;
      const bLive = b.status === "connected" && b.hubOnline ? 0 : 1;
      if (aLive !== bLive) return aLive - bLive;
      const v = a.venueName.localeCompare(b.venueName);
      if (v !== 0) return v;
      return a.name.localeCompare(b.name);
    });
  }, [displays]);

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handlePush(): Promise<void> {
    if (selected.size === 0) return;
    try {
      const result = await push.mutate({
        id: leaderboardId,
        displayAgentIds: Array.from(selected),
      });
      const offline = result.hubsOffline.length;
      const summary =
        offline > 0
          ? `Pushed to ${result.displaysScheduled} of ${selected.size} displays — ${offline} hub${offline === 1 ? "" : "s"} offline`
          : `Pushed to ${result.displaysScheduled} display${result.displaysScheduled === 1 ? "" : "s"}`;
      addToast(summary);
      if (result.rejected.length > 0) {
        addToast(`${result.rejected.length} display(s) rejected (wrong org or unknown)`, "error");
      }
      onClose();
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Push failed", "error");
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Push "${leaderboardName}"`}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Pick the displays to project this leaderboard onto. Multiple venues
          are fine — each display will load the same cross-venue view.
        </p>

        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
            Loading displays…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load displays: {error}
          </div>
        ) : sortedDisplays.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
            No display agents registered to any venue in this organisation.
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 max-h-72 overflow-y-auto divide-y divide-gray-100">
            {sortedDisplays.map((d) => (
              <DisplayRow key={d.id} d={d} checked={selected.has(d.id)} onToggle={() => toggle(d.id)} />
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={push.loading}>
            Cancel
          </Button>
          <Button onClick={handlePush} loading={push.loading} disabled={selected.size === 0}>
            Push{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DisplayRow({
  d,
  checked,
  onToggle,
}: {
  d: OrgDisplayAgent;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  const offline = d.status !== "connected" || !d.hubOnline;
  return (
    <label
      className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${offline ? "opacity-70" : ""}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">{d.name}</span>
          <StatusBadge domain="server" status={d.status === "connected" ? "connected" : "offline"} />
          {!d.hubOnline && (
            <span className="text-[10px] uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 rounded px-1.5">
              hub offline
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{d.venueName}</p>
      </div>
    </label>
  );
}
